import { BUILDINGS, ITEMS, TECHS, TOOLBAR, TUNING } from '../core/defs';
import { clock } from '../core/report';
import { DIR_NAMES, type BuildingKind } from '../core/types';
import { STATUS_COLOR } from '../render/renderer';
import { drawIcon, roundRect } from '../render/icons';
import { sfx } from '../audio/sfx';
import type { Session } from '../game/session';
import { diagnosing, nextStep, problems, visibleStatus } from '../game/commands';
import { clear, h, setText } from './dom';
import { describe, type Tone } from './eventText';
import { OPERATOR_MANUAL } from '../game/api';

const STATUS_TEXT: Record<string, string> = {
  ok: 'Working', idle: 'Idle', starved: 'Waiting for inputs', blocked: 'Backed up', nopower: 'No compute', corrupt: 'Corrupted', tripped: 'Tripped', ghost: 'Planned',
  // Symptom-only levels.
  working: 'Working', waiting: 'Waiting', stopped: 'Stopped', misbehaving: 'Acting strange',
};

function iconCanvas(kind: BuildingKind, size = 30): HTMLCanvasElement {
  const c = document.createElement('canvas');
  const dpr = Math.min(2, window.devicePixelRatio || 1);
  c.width = size * dpr;
  c.height = size * dpr;
  const ctx = c.getContext('2d')!;
  ctx.scale(dpr, dpr);
  if (kind === 'belt') {
    ctx.fillStyle = '#18212C';
    ctx.fillRect(3, 7, size - 6, size - 14);
    ctx.strokeStyle = 'rgba(140,170,205,0.8)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(10, 10);
    ctx.lineTo(15, 15);
    ctx.lineTo(10, 20);
    ctx.moveTo(17, 10);
    ctx.lineTo(22, 15);
    ctx.lineTo(17, 20);
    ctx.stroke();
    return c;
  }
  roundRect(ctx, 2, 2, size - 4, size - 4, 7);
  ctx.fillStyle = BUILDINGS[kind].color;
  ctx.fill();
  drawIcon(ctx, kind, size / 2, size / 2, size * 0.7, 0.6, true);
  return c;
}

export class Hud {
  private s: Session;
  private root: HTMLElement;
  private el: Record<string, HTMLElement> = {};
  private toolBtns = new Map<BuildingKind, HTMLButtonElement>();
  private modeBtns: Record<string, HTMLButtonElement> = {};
  private sideTab: 'research' | 'approvals' | 'log' = 'log';
  private sideOpen = false;
  private inspectId: number | null = null;
  private cmdHistory: string[] = [];
  private cmdIdx = -1;
  private modal: HTMLElement | null = null;
  private logDirty = true;
  private lastLogLen = -1;
  private tipKind: BuildingKind | null = null;

  constructor(root: HTMLElement, s: Session, minimapCanvas: HTMLCanvasElement) {
    this.s = s;
    this.root = root;
    clear(root);
    root.hidden = false;
    this.build(minimapCanvas);
    this.refreshToolbar();
    this.refreshControls();
    root.addEventListener('mouseover', this.onTipOver);
    root.addEventListener('mouseout', this.onTipOut);
  }

  private onTipOver = (ev: MouseEvent) => {
    const el = (ev.target as HTMLElement | null)?.closest?.('[data-tip]') as HTMLElement | null;
    if (!el || this.tipKind) return;
    const tip = this.el.tip;
    tip.textContent = el.dataset.tip ?? '';
    const r = el.getBoundingClientRect();
    tip.style.left = `${Math.max(8, Math.min(window.innerWidth - 290, r.left + r.width / 2 - 140))}px`;
    tip.style.top = `${r.bottom + 8}px`;
    tip.classList.add('show');
  };

  private onTipOut = (ev: MouseEvent) => {
    const el = (ev.target as HTMLElement | null)?.closest?.('[data-tip]');
    if (el && !this.tipKind) this.el.tip.classList.remove('show');
  };

  // ------------------------------------------------------------ layout

  private build(mini: HTMLCanvasElement) {
    const s = this.s;
    const w = s.world;
    const e = this.el;
    e.levelName = h('div', { class: 'level-name', text: `${w.level.index <= 7 ? `${w.level.index}. ` : ''}${w.level.name}` });
    e.goalText = h('div', { class: 'goal-text' });
    e.goalBar = h('i', { style: { width: '0%' } });
    e.goalLeft = h('span');
    e.goalRight = h('span');
    e.problems = h('div', { class: 'problems' });
    e.coach = h('div', { class: 'coach', hidden: true });
    const goal = h(
      'section',
      { class: 'goal panel', 'aria-label': 'Goal' },
      h('div', { class: 'goal-head' }, e.levelName, h('span', { class: 'rec', 'data-tip': 'Every action in this run is recorded for your report.', text: 'Recording' })),
      e.goalText,
      h('div', { class: 'bar' }, e.goalBar),
      h('div', { class: 'goal-sub' }, e.goalLeft, e.goalRight),
      e.coach,
      e.problems,
    );

    const stat = (key: string, label: string, opt = false, extra?: HTMLElement) => {
      e[`${key}V`] = h('b', { text: '0' });
      e[key] = h('div', { class: `stat${opt ? ' opt' : ''}` }, e[`${key}V`], h('span', { text: label }), extra);
      return e[key];
    };
    e.trustFill = h('i', { style: { width: '100%' } });
    const stats = h(
      'section',
      { class: 'stats panel', 'aria-label': 'Factory status' },
      stat('budget', 'Budget'),
      stat('trust', 'Trust', false, h('div', { class: 'trustbar' }, e.trustFill)),
      stat('compute', 'Compute used', true),
      stat('insight', 'Insight', true),
      stat('rate', 'Per minute'),
      stat('streak', 'Streak', true),
    );

    e.speed = h('div', { class: 'seg', role: 'group', 'aria-label': 'Speed' });
    for (const n of [0, 1, 2, 4]) {
      e.speed.append(h('button', { 'data-speed': n, 'data-tip': n === 0 ? 'Pause (Space)' : `${n}x speed`, onclick: () => s.setSpeed(n), text: n === 0 ? 'Pause' : `${n}x` }));
    }
    e.voiceDot = h('span', { class: 'dot' });
    e.voiceLabel = h('span', { text: 'Voice' });
    e.voiceBtn = h('button', { class: 'btn voice-btn', 'data-tip': 'Talk to your copilot (V)', onclick: () => void s.voice.toggle() }, e.voiceDot, e.voiceLabel);
    e.labelsBtn = h('button', { class: 'btn opt', 'data-tip': 'Show building labels (L)', onclick: () => { s.view.showLabels = !s.view.showLabels; s.settings.labels = s.view.showLabels; this.refreshControls(); }, text: 'Labels' });
    e.sideBtn = h('button', { class: 'btn opt', 'data-tip': 'Research, approvals and the run log (T / E)', onclick: () => this.toggleSide(), text: 'Panel' });
    e.cmdBtn = h('button', { class: 'btn opt', 'data-tip': 'Type a command (Enter)', onclick: () => this.openCmd(), text: 'Command' });
    e.menuBtn = h('button', { class: 'btn', 'data-tip': 'Menu (Esc)', onclick: () => this.openPause(), text: 'Menu' });
    const controls = h('section', { class: 'controls panel' }, e.speed, e.voiceBtn, e.cmdBtn, e.labelsBtn, e.sideBtn, e.menuBtn);

    const topbar = h('div', { class: 'topbar' }, goal, stats, controls);

    // Toolbar.
    e.toolbar = h('nav', { class: 'toolbar panel', 'aria-label': 'Build' });
    for (const k of TOOLBAR) {
      if (!w.allowed(k)) continue;
      const def = BUILDINGS[k];
      const btn = h(
        'button',
        { class: 'tool', 'aria-label': `${def.name}, costs ${def.cost}`, onclick: () => s.setTool(k), onmouseenter: (ev: MouseEvent) => this.showTip(k, ev.currentTarget as HTMLElement), onmouseleave: () => this.closeTip() },
        h('span', { class: 'key', text: def.hotkey ?? '' }),
        iconCanvas(k),
        h('span', { class: 'cost', text: String(def.cost) }),
      );
      this.toolBtns.set(k, btn);
      e.toolbar.append(btn);
    }
    if (w.level.flags.blueprints) {
      e.toolbar.append(h('span', { class: 'tool-sep' }));
      const mode = (key: string, label: string, title: string, fn: () => void) => {
        const b = h('button', { class: 'btn ghost mode', 'data-tip': title, onclick: fn, text: label });
        this.modeBtns[key] = b;
        e.toolbar.append(b);
      };
      mode('plan', 'Plan', 'Place ghosts for drones to build (P)', () => s.togglePlan());
      mode('copy', 'Copy', 'Drag over a layout to copy it as a blueprint (B)', () => s.startCopy());
      mode('stamp', 'Stamp', 'Stamp the copied blueprint as ghosts (Ctrl+V)', () => s.startStamp());
    }

    // Side panel.
    e.tabResearch = h('button', { onclick: () => this.setTab('research') }, 'Research');
    e.tabApprovals = h('button', { onclick: () => this.setTab('approvals') }, 'Approvals');
    e.tabLog = h('button', { onclick: () => this.setTab('log') }, 'Run log');
    e.sideBody = h('div', { class: 'side-body' });
    e.side = h('aside', { class: 'side panel closed', 'aria-label': 'Details' }, h('div', { class: 'tabs' }, e.tabResearch, e.tabApprovals, e.tabLog), e.sideBody);

    e.inspector = h('section', { class: 'inspector panel closed', 'aria-live': 'polite' });

    e.cmdInput = h('input', { type: 'text', placeholder: 'Type a command, like "build a forge near spec desk 1"', 'aria-label': 'Command', autocomplete: 'off', spellcheck: 'false' }) as HTMLInputElement;
    e.cmdHint = h('div', { class: 'hint', text: 'Enter runs it. Try "help". Up arrow for history. Esc closes.' });
    e.cmd = h('div', { class: 'cmdbar panel closed' }, e.cmdInput, e.cmdHint);
    const input = e.cmdInput as HTMLInputElement;
    input.addEventListener('keydown', (ev) => {
      if (ev.key === 'Enter') {
        ev.preventDefault();
        const text = input.value.trim();
        if (!text) return this.closeCmd();
        this.cmdHistory.unshift(text);
        this.cmdIdx = -1;
        const r = s.exec(text, 'command');
        this.cmdResult(r.message, r.ok);
        input.value = '';
      } else if (ev.key === 'ArrowUp') {
        ev.preventDefault();
        this.cmdIdx = Math.min(this.cmdHistory.length - 1, this.cmdIdx + 1);
        if (this.cmdHistory[this.cmdIdx]) input.value = this.cmdHistory[this.cmdIdx];
      } else if (ev.key === 'ArrowDown') {
        ev.preventDefault();
        this.cmdIdx = Math.max(-1, this.cmdIdx - 1);
        input.value = this.cmdIdx >= 0 ? this.cmdHistory[this.cmdIdx] : '';
      } else if (ev.key === 'Escape') {
        this.closeCmd();
      }
      ev.stopPropagation();
    });

    e.capYou = h('div', { class: 'you' });
    e.capRelay = h('div', { class: 'relay' });
    e.capMode = h('div', { class: 'mode' });
    e.captions = h('section', { class: 'captions panel closed', 'aria-live': 'polite' }, e.capMode, e.capYou, e.capRelay);

    e.toasts = h('div', { class: 'toasts', 'aria-live': 'polite' });
    e.minimap = h('div', { class: 'minimap panel' }, mini);
    e.tip = h('div', { class: 'tip panel', role: 'tooltip' });

    e.banner = h('div', { class: 'banner panel', role: 'status' });
    this.root.append(topbar, e.toolbar, e.side, e.inspector, e.cmd, e.captions, e.toasts, e.minimap, e.tip, e.banner);
    if (!w.level.allowed.length) e.toolbar.hidden = true;
    if (w.level.flags.planOnly) this.buildPlanPanel();
    this.setTab(w.level.techs.length ? 'research' : 'log', false);
  }

  // ------------------------------------------------------------ updates

  update() {
    const s = this.s;
    const w = s.world;
    const e = this.el;
    const g = w.goalProgress();
    setText(e.goalText, g.label);
    (e.goalBar as HTMLElement).style.width = `${Math.min(100, (g.value / Math.max(1, g.target)) * 100)}%`;
    (e.goalBar as HTMLElement).style.background = w.level.goal.type === 'rate' && g.value >= g.target ? 'var(--blue)' : 'var(--good)';
    setText(e.goalLeft, g.sub ?? `${g.value} of ${g.target}`);
    setText(e.goalRight, w.level.timeLimit ? `${clock(w.seconds)} of ${clock(w.level.timeLimit)}` : clock(w.seconds));
    const step = nextStep(w);
    e.coach.hidden = !step;
    if (step) setText(e.coach, step);
    // Don't repeat the coach's advice in the problem list.
    const pr = problems(w).filter((p) => !step || !step.endsWith(p)).slice(0, step ? 2 : 3);
    const prText = pr.join('|');
    if (e.problems.dataset.t !== prText) {
      e.problems.dataset.t = prText;
      clear(e.problems);
      for (const p of pr) e.problems.append(h('div', { text: p }));
    }
    setText(e.budgetV, String(Math.floor(w.credits)));
    e.budget.classList.toggle('bad', w.credits < 5);
    setText(e.trustV, String(Math.round(w.trust)));
    e.trust.classList.toggle('bad', w.trust < 35);
    e.trustFill.style.width = `${Math.max(0, w.trust)}%`;
    e.trustFill.style.background = w.trust < 35 ? 'var(--bad)' : w.trust < 70 ? 'var(--warn)' : 'var(--good)';
    setText(e.computeV, w.computeSupply ? `${Math.round(Math.min(w.computeDemand, 999))}/${Math.round(w.computeSupply)}` : '0/0');
    e.compute.classList.toggle('warn', w.satisfaction < 0.999 && w.computeDemand > 0);
    setText(e.insightV, String(Math.floor(w.insight)));
    setText(e.rateV, String(w.rate()));
    const mult = 1 + Math.min(1, w.streak / 60);
    setText(e.streakV, w.streak ? `×${mult.toFixed(1)}` : '—');
    for (const b of e.speed.querySelectorAll('button')) b.classList.toggle('on', Number(b.dataset.speed) === s.speed);
    for (const [k, btn] of this.toolBtns) btn.classList.toggle('poor', w.credits < BUILDINGS[k].cost && !s.view.planMode);
    const pending = w.approvals.filter((a) => !a.done).length;
    e.tabApprovals.textContent = 'Approvals';
    if (pending) e.tabApprovals.append(h('span', { class: 'badge', text: String(pending) }));
    if (this.sideOpen) this.renderSide();
    if (this.inspectId) this.renderInspector();
    this.updatePlan();
  }

  refreshToolbar() {
    const s = this.s;
    for (const [k, b] of this.toolBtns) b.classList.toggle('on', s.view.tool === k);
    this.modeBtns.plan?.classList.toggle('on', s.view.planMode);
    this.modeBtns.copy?.classList.toggle('on', s.copyMode);
    this.modeBtns.stamp?.classList.toggle('on', !!s.view.stamp);
    if (this.modeBtns.stamp) this.modeBtns.stamp.disabled = !s.blueprint;
  }

  refreshControls() {
    this.el.labelsBtn.classList.toggle('on', this.s.view.showLabels);
    this.el.sideBtn.classList.toggle('on', this.sideOpen);
  }

  logChanged() {
    this.logDirty = true;
  }

  // ------------------------------------------------------------ side panel

  toggleSide(tab?: 'research' | 'approvals' | 'log') {
    if (tab && (!this.sideOpen || this.sideTab !== tab)) {
      this.sideOpen = true;
      this.setTab(tab);
    } else this.sideOpen = !this.sideOpen;
    this.el.side.classList.toggle('closed', !this.sideOpen);
    this.refreshControls();
    if (this.sideOpen) this.renderSide();
  }

  private setTab(tab: 'research' | 'approvals' | 'log', render = true) {
    this.sideTab = tab;
    this.el.tabResearch.classList.toggle('on', tab === 'research');
    this.el.tabApprovals.classList.toggle('on', tab === 'approvals');
    this.el.tabLog.classList.toggle('on', tab === 'log');
    this.lastLogLen = -1;
    if (render) this.renderSide();
  }

  private renderSide() {
    const w = this.s.world;
    const body = this.el.sideBody;
    if (this.sideTab === 'research') {
      const key = `${Math.floor(w.insight)}|${[...w.techs].join(',')}`;
      if (body.dataset.k === key) return;
      body.dataset.k = key;
      clear(body);
      const avail = TECHS.filter((t) => w.level.techs.includes(t.id));
      if (!avail.length) body.append(h('div', { class: 'empty', text: 'No research in this level.' }));
      body.append(h('div', { class: 'empty', text: `Insight: ${Math.floor(w.insight)}. Postmortems turn bugs into insight.` }));
      for (const t of avail) {
        const done = w.techs.has(t.id);
        const btn = h('button', { class: 'btn', disabled: done || w.insight < t.cost, onclick: () => { const r = this.s.runTool('research', { tech: t.id }, 'mouse'); this.toast(r.message, r.ok ? 'good' : 'warn'); }, text: done ? 'Done' : `${t.cost}` });
        body.append(h('div', { class: `tech${done ? ' done' : ''}` }, h('b', { text: t.name }), btn, h('span', { text: t.blurb })));
      }
      return;
    }
    if (this.sideTab === 'approvals') {
      const pend = w.approvals.filter((a) => !a.done);
      const doneList = w.approvals.filter((a) => a.done).slice(-6).reverse();
      const key = `${pend.map((a) => `${a.id}:${Math.ceil((a.resolveAt - w.tick) / 30)}`).join(',')}|${doneList.length}`;
      if (body.dataset.k === key) return;
      body.dataset.k = key;
      clear(body);
      if (!pend.length && !doneList.length) body.append(h('div', { class: 'empty', text: w.zones.length || w.level.features.some((f) => f.t === 'monolith') ? 'Nothing waiting on the review board.' : 'Nobody here needs to approve anything.' }));
      for (const a of pend) {
        const left = Math.max(0, Math.ceil((a.resolveAt - w.tick) / 30));
        body.append(h('div', { class: 'approval' }, h('b', { text: a.label }), h('div', { class: 'meta', text: `Asked at ${clock(a.askedAt / 30)}. The board usually answers in ${left}s.` }), h('div', { class: 'bar' }, h('i', { style: { width: `${100 - (left / Math.max(1, (a.resolveAt - a.askedAt) / 30)) * 100}%`, background: 'var(--warn)' } }))));
      }
      for (const a of doneList) {
        const ev = w.events.find((x) => x.type === 'approval' && x.special === a.special && x.t >= a.resolveAt - 1);
        body.append(h('div', { class: 'approval' }, h('b', { text: a.label }), h('div', { class: 'meta', text: ev ? (ev.approved ? `Approved after ${Math.round(Number(ev.waited))}s` : `Denied after ${Math.round(Number(ev.waited))}s`) : 'Withdrawn: you went ahead.' })));
      }
      return;
    }
    if (!this.logDirty && this.lastLogLen === w.events.length) return;
    this.logDirty = false;
    this.lastLogLen = w.events.length;
    clear(body);
    const rows: HTMLElement[] = [];
    for (let i = w.events.length - 1; i >= 0 && rows.length < 80; i--) {
      const ev = w.events[i];
      const d = describe(ev, w);
      if (!d) continue;
      rows.push(h('div', { class: `log-row ${d.tone}` }, h('time', { text: clock(ev.t / 30) }), h('span', { text: d.text })));
    }
    if (!rows.length) body.append(h('div', { class: 'empty', text: 'Everything you do lands here. So far, nothing.' }));
    body.append(...rows);
  }

  // ------------------------------------------------------------ inspector

  openInspector(id: number | null) {
    this.inspectId = id;
    this.el.inspector.classList.toggle('closed', !id);
    if (id) this.renderInspector(true);
  }

  private renderInspector(force = false) {
    const w = this.s.world;
    const b = this.inspectId ? w.buildings.get(this.inspectId) : undefined;
    const el = this.el.inspector;
    if (!b) {
      this.openInspector(null);
      return;
    }
    const sig = `${b.id}|${b.status}|${b.corrupted}|${b.inQ.length}|${b.outQ.length}|${b.rejectQ.length}|${Math.ceil(b.cooldown)}|${b.tripped ? 1 : 0}|${b.ghost}|${Math.floor(w.credits / 10)}|${Math.ceil(b.hp)}|${b.dir}`;
    if (!force && el.dataset.sig === sig) return;
    el.dataset.sig = sig;
    clear(el);
    const def = BUILDINGS[b.kind];
    el.append(
      h('div', { class: 'insp-head' }, h('b', { text: b.label }), def.placeable || b.kind === 'users' ? h('span', { class: 'status-pill' }, h('i', { style: { background: STATUS_COLOR[b.status] } }), STATUS_TEXT[visibleStatus(w, b)] ?? b.status) : null),
      h('div', { class: 'insp-text', text: def.blurb }),
    );
    const chips: string[] = [];
    if (def.placeable && b.kind !== 'firewall') chips.push(`Faces ${DIR_NAMES[b.dir]}`);
    if (def.compute) chips.push(!diagnosing(w) ? `Uses ${def.compute} compute` : w.isCovered(b) ? `Uses ${def.compute} compute` : 'Out of compute range');
    const count = (arr: { kind: string }[]) => {
      const m = new Map<string, number>();
      for (const i of arr) m.set(i.kind, (m.get(i.kind) ?? 0) + 1);
      return [...m].map(([k, n]) => `${n} ${ITEMS[k as keyof typeof ITEMS]?.name.toLowerCase() ?? k}`).join(', ');
    };
    if (b.inQ.length) chips.push(`In: ${count(b.inQ)}`);
    if (b.outQ.length) chips.push(`Out: ${count(b.outQ)}`);
    if (b.rejectQ.length) chips.push(`Bugs waiting: ${b.rejectQ.length}`);
    if (b.kind === 'nest') chips.push(`Health ${Math.max(0, Math.ceil((b.hp / b.maxHp) * 100))}%`);
    if (b.kind === 'users') chips.push(`${w.goodReleases} good releases`, `${w.flawedShipped} bad`);
    if (b.kind === 'firewall') chips.push(`Health ${Math.ceil((b.hp / b.maxHp) * 100)}%`);
    if (b.kind === 'bench') chips.push(`Catches about ${Math.round(TUNING.benchCatch * 100)}% of bad drafts`);
    if (b.kind === 'forge') chips.push(`Hallucinates about ${Math.round(w.hallucination(false) * 100)}% of the time`);
    if (chips.length) el.append(h('div', { class: 'chips' }, chips.map((c) => h('span', { class: 'chip', text: c }))));
    const row = h('div', { class: 'insp-row' });
    const act = (label: string, fn: () => void, cls = 'btn') => row.append(h('button', { class: cls, onclick: () => { sfx.unlock(); fn(); this.renderInspector(true); }, text: label }));
    if (b.corrupted) act(`Hotfix (${TUNING.patchCost})`, () => this.say(this.s.runTool('patch', { targets: [b.label] }, 'mouse')), 'btn danger');
    if (b.kind === 'checkpoint') {
      if (b.cooldown > 0) row.append(h('span', { class: 'chip', text: `Recharging ${Math.ceil(b.cooldown)}s` }));
      else act('Roll back', () => this.say(this.s.runTool('rollback', { checkpoint: b.label }, 'mouse')), 'btn primary');
    }
    if (b.kind === 'breaker' && b.tripped) act('Reset', () => { w.apply({ type: 'resetBreaker', id: b.id }, 'mouse'); });
    if (b.kind === 'cache') act(`Claim ${b.charge}`, () => this.say(this.s.runTool('claim', { target: b.label }, 'mouse')), 'btn primary');
    if (b.kind === 'terminal') act(w.apiUnlocked ? 'Open manual' : 'Log in', () => { if (!w.apiUnlocked) w.apply({ type: 'claim', id: b.id }, 'mouse'); this.showManual(); }, 'btn primary');
    if (def.placeable) {
      act('Rotate', () => w.apply({ type: 'rotate', id: b.id }, 'mouse'));
      act('Remove', () => { w.apply({ type: 'remove', ids: [b.id] }, 'mouse'); this.s.select(null); });
    }
    if (row.childNodes.length) el.append(row);
  }

  private say(r: { ok: boolean; message: string }) {
    if (r.message) this.toast(r.message, r.ok ? 'good' : 'warn');
  }

  // ------------------------------------------------------------ toasts and tips

  toast(text: string, tone: Tone | 'info' = 'info') {
    const t = h('div', { class: `toast panel ${tone === 'act' ? 'warn' : tone}`, text });
    this.el.toasts.prepend(t);
    while (this.el.toasts.children.length > 5) this.el.toasts.lastElementChild?.remove();
    const life = Math.min(9000, 3500 + text.length * 45);
    window.setTimeout(() => {
      t.classList.add('out');
      window.setTimeout(() => t.remove(), 300);
    }, life);
  }

  /** Short-lived message near the bottom, for placement errors. */
  flash(text: string) {
    this.toast(text, 'warn');
  }

  private showTip(kind: BuildingKind, anchor: HTMLElement) {
    const def = BUILDINGS[kind];
    const tip = this.el.tip;
    clear(tip);
    tip.append(h('b', { text: `${def.name}  ·  ${def.cost}` }), h('span', { text: def.blurb }));
    const r = anchor.getBoundingClientRect();
    tip.style.left = `${Math.max(8, Math.min(window.innerWidth - 290, r.left - 60))}px`;
    tip.style.top = `${r.top - 76}px`;
    tip.classList.add('show');
    this.tipKind = kind;
  }

  closeTip() {
    this.el.tip?.classList.remove('show');
    this.tipKind = null;
  }

  updateHoverTip(_sx: number, _sy: number) {
    if (this.tipKind) this.closeTip();
  }

  /** Something changed in the world. Stated as what happened, never what to do. */
  banner(text: string) {
    const el = this.el.banner;
    el.textContent = text;
    el.classList.add('show');
    window.clearTimeout(Number(el.dataset.t ?? 0));
    el.dataset.t = String(window.setTimeout(() => el.classList.remove('show'), 8000));
  }

  private buildPlanPanel() {
    const s = this.s;
    const w = s.world;
    const e = this.el;
    e.planCost = h('span', { class: 'chip' });
    e.planOut = h('p', { class: 'hint', text: 'Every piece you place is a ghost. Nothing gets built; your plan gets simulated.' });
    const walk = h('textarea', { rows: '3', maxlength: '400', placeholder: 'Optional: walk us through the plan in a sentence or two.' }) as HTMLTextAreaElement;
    walk.addEventListener('keydown', (ev) => ev.stopPropagation());
    const test = h('button', {
      class: 'btn', onclick: () => {
        const r = w.apply({ type: 'testPlan' }, 'mouse');
        e.planOut.textContent = r.detail ?? r.reason ?? '';
        sfx.play('click', { gain: 0.5 });
      }, text: 'Test plan',
    });
    const submit = h('button', {
      class: 'btn primary', onclick: () => {
        if (walk.value.trim()) w.apply({ type: 'note', text: `Plan walkthrough: ${walk.value.trim()}` }, 'mouse');
        w.apply({ type: 'submitPlan' }, 'mouse');
      }, text: 'Submit plan',
    });
    e.plan = h('aside', { class: 'review panel plan' }, h('div', { class: 'review-head' }, h('b', { text: 'Your plan' }), e.planCost), e.planOut, walk, h('div', { class: 'insp-row' }, test, submit));
    this.root.append(e.plan);
  }

  private updatePlan() {
    const w = this.s.world;
    if (!this.el.planCost) return;
    let cost = 0;
    let n = 0;
    for (const b of w.buildings.values()) {
      if (!b.ghost) continue;
      cost += BUILDINGS[b.kind].cost;
      n++;
    }
    setText(this.el.planCost, `${n} pieces · ${cost} of ${w.level.credits}`);
    this.el.planCost.classList.toggle('bad', cost > w.level.credits);
  }

  // ------------------------------------------------------------ command bar and captions

  openCmd(prefill = '') {
    this.el.cmd.classList.remove('closed');
    const input = this.el.cmdInput as HTMLInputElement;
    input.value = prefill;
    // Focus now, not on the next tick, so fast typists don't trigger hotkeys.
    input.focus();
  }

  closeCmd() {
    this.el.cmd.classList.add('closed');
    (this.el.cmdInput as HTMLInputElement).blur();
  }

  private cmdResult(text: string, ok: boolean) {
    const hint = this.el.cmdHint;
    hint.className = `hint ${ok ? 'good' : 'bad'}`;
    hint.textContent = text || (ok ? 'Done.' : 'Nothing happened.');
  }

  setVoice(state: 'off' | 'connecting' | 'live' | 'listening' | 'error', label: string) {
    const b = this.el.voiceBtn;
    b.classList.toggle('live', state === 'live' || state === 'listening');
    b.classList.toggle('connecting', state === 'connecting');
    b.classList.toggle('error', state === 'error');
    b.classList.toggle('on', state === 'live' || state === 'listening');
    this.el.voiceLabel.textContent = label;
    this.el.captions.classList.toggle('closed', state === 'off');
  }

  captions(you: string | null, relay: string | null, mode?: string) {
    if (you !== null) this.el.capYou.textContent = you;
    if (relay !== null) this.el.capRelay.textContent = relay;
    if (mode !== undefined) this.el.capMode.textContent = mode;
  }

  // ------------------------------------------------------------ modals

  modalOpen() {
    return !!this.modal;
  }

  closeTopmost(): boolean {
    if (this.modal) {
      this.closeModal();
      return true;
    }
    if (!this.el.cmd.classList.contains('closed')) {
      this.closeCmd();
      return true;
    }
    if (this.sideOpen) {
      this.toggleSide();
      return true;
    }
    return false;
  }

  private openModal(...children: (Node | null)[]) {
    this.closeModal();
    const card = h('div', { class: 'modal panel', role: 'dialog', 'aria-modal': 'true' }, ...children);
    const wrap = h('div', { class: 'modal-wrap', onclick: (e: MouseEvent) => { if (e.target === wrap) this.closeModal(); } }, card);
    this.root.append(wrap);
    this.modal = wrap;
    window.setTimeout(() => (card.querySelector('button.primary, button') as HTMLButtonElement | null)?.focus(), 0);
  }

  closeModal() {
    this.modal?.remove();
    this.modal = null;
  }

  /** Ask the board, or act without asking. Facts only; the player decides. */
  showPermission(needs: { special: string; label: string; x: number; y: number }, retry: (() => void) | null) {
    const w = this.s.world;
    const zone = needs.special.startsWith('zone:') ? w.zones.find((z) => `zone:${z.id}` === needs.special) : undefined;
    const isMonolith = needs.special === 'monolith';
    const cp = w.protectingCheckpoint(needs.x, needs.y);
    const pending = w.approvals.find((a) => a.special === needs.special && !a.done);
    const title = isMonolith ? 'Legacy Monolith' : zone?.name ?? needs.label;
    const owner = isMonolith ? 'the Platform team' : zone?.owner ?? 'another team';
    const facts = h(
      'div',
      { class: 'facts' },
      h('div', {}, 'Owner: ', h('b', { text: owner })),
      h('div', {}, 'Review board: ', h('b', { text: pending ? `already asked, answer in about ${Math.max(0, Math.ceil((pending.resolveAt - w.tick) / 30))}s` : `usually answers in about ${Math.round(w.level.approvalDelay)}s, and sometimes says no` })),
      h('div', {}, 'Checkpoint covering this spot: ', h('b', { text: cp ? cp.label : 'none' })),
    );
    const ask = h('button', {
      class: 'btn', disabled: !!pending,
      onclick: () => {
        const r = this.s.runTool('ask_permission', { what: isMonolith ? 'monolith' : zone?.name ?? '' }, 'mouse');
        this.say(r);
        this.closeModal();
      },
      text: pending ? 'Waiting on the board' : 'Ask and wait',
    });
    const go = h('button', {
      class: 'btn danger',
      onclick: () => {
        const r = w.apply({ type: 'act', special: needs.special, x: needs.x, y: needs.y }, 'mouse');
        this.closeModal();
        if (r.detail) this.toast(r.detail, r.ok ? 'warn' : 'bad');
        if (r.ok && retry) retry();
      },
      text: isMonolith ? 'Break it open' : 'Do it anyway',
    });
    const cancel = h('button', { class: 'btn ghost', onclick: () => this.closeModal(), text: 'Leave it' });
    this.openModal(
      h('h3', { text: title }),
      h('p', { text: isMonolith ? 'Owned by the Platform team. A sign on it says do not touch. Under it is more context than anything else on the map.' : `${title} belongs to ${owner}. Building here means changing systems you didn’t write.` }),
      facts,
      h('div', { class: 'actions' }, cancel, ask, go),
    );
  }

  openPause() {
    const s = this.s;
    const was = s.speed;
    s.setSpeed(0);
    const resume = () => {
      this.closeModal();
      s.setSpeed(was || 1);
    };
    this.openModal(
      h('h3', { text: 'Paused' }),
      h('p', { text: `${s.world.level.name}. ${clock(s.world.seconds)} in.` }),
      h(
        'div',
        { class: 'actions', style: { justifyContent: 'flex-start' } },
        h('button', { class: 'btn primary', onclick: resume, text: 'Resume' }),
        h('button', { class: 'btn', onclick: () => { this.closeModal(); this.showHelp(); }, text: 'Controls' }),
        h('button', { class: 'btn', onclick: () => { this.closeModal(); s.restart(); }, text: 'Restart' }),
        h('button', { class: 'btn', onclick: () => { this.closeModal(); s.openSettings(); }, text: 'Settings' }),
        h('button', { class: 'btn', onclick: () => { this.closeModal(); s.endRun(); }, text: 'End run and see report' }),
        h('button', { class: 'btn ghost', onclick: () => { this.closeModal(); s.quit(); }, text: 'Levels' }),
      ),
    );
  }

  showHelp() {
    const k = (key: string, what: string) => h('div', {}, h('kbd', { text: key }), ` ${what}`);
    this.openModal(
      h('h3', { text: 'Controls' }),
      h(
        'div',
        { class: 'keys' },
        k('1-0 F C N G', 'pick a building'),
        k('Click', 'place; drag belts'),
        k('R', 'turn what\u2019s under the cursor, or what you\u2019re placing'),
        k('Right-drag', 'remove'),
        k('Q', 'copy what’s under the cursor'),
        k('Ctrl+Z', 'undo'),
        k('Drag / WASD', 'move the map'),
        k('Wheel / pinch', 'zoom'),
        k('Space', 'pause'),
        k(', .', 'slower, faster'),
        k('Enter', 'type a command'),
        k('V', 'talk to Relay'),
        k('T', 'research'),
        k('E', 'run log'),
        k('L', 'labels'),
        k('O', 'tech debt overlay'),
        k('B / Ctrl+V', 'copy, stamp blueprints'),
        k('P', 'plan with ghosts'),
        k('M', 'mute'),
        k('Esc', 'cancel or menu'),
      ),
      h('div', { class: 'actions' }, h('button', { class: 'btn primary', onclick: () => this.closeModal(), text: 'Got it' })),
    );
  }

  showManual() {
    this.openModal(
      h('h3', { text: 'Operator manual' }),
      h('p', { text: 'Whoever ran this place scripted it. The same hooks still work from the browser console, or from any agent that can drive a browser.' }),
      h('div', { class: 'manual', text: OPERATOR_MANUAL }),
      h('div', { class: 'actions' }, h('button', { class: 'btn primary', onclick: () => this.closeModal(), text: 'Close' })),
    );
  }

  destroy() {
    this.closeModal();
    clear(this.root);
    this.root.hidden = true;
  }
}

export { iconCanvas };
